import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import DownlineDetailsPanel from './DownlineDetailsPanel';
import CommissionBreadcrumb from './CommissionBreadcrumb';

// Interfaces
interface IntermediateUplineTakeDto {
  userId: number;
  username: string;
  fullName: string;
  level: number;
  takePercentage: number;
}

interface HierarchicalOperatorCommissionDto {
  id: number;
  hierarchicalCommissionId: number;
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  ownTakePercentage: number;
  maxAllowedPercentage: number;
  baseAllocationPercentage: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  notes?: string;
  // Calculated fields from backend
  basePoolPercentage: number;
  currentUserTakePercentage: number;
  intermediateUplinesTotalPercentage: number;
  intermediateUplines: IntermediateUplineTakeDto[];
  totalTakenPercentage: number;
  remainingPercentage: number;
  maxCurrentUserCanTake: number;
}

interface HierarchicalDownlineDto {
  id: number;
  userId: number;
  username: string;
  fullName: string;
  email: string;
  parentUserId?: number;
  parentUsername?: string;
  ownTakePercentage: number;
  maxAllowedPercentage: number;
  baseAllocationPercentage: number;
  availableForUplines: number;
  hierarchyLevel: number;
  canCreateDownlines: boolean;
  createdAt: string;
  isActive: boolean;
  notes?: string;
  myTakeFromThem: number;
  remainingForMe: number;
  operatorCommissions: HierarchicalOperatorCommissionDto[];
  downlines: HierarchicalDownlineDto[];
  totalDownlinesCount: number;
  directDownlinesCount: number;
  hasDownlines: boolean;
}

interface HierarchicalDownlineResponseDto {
  currentUserId: number;
  currentUsername: string;
  currentFullName: string;
  currentLevel: number;
  directDownlines: HierarchicalDownlineDto[];
  totalDownlinesCount: number;
  maxDepth: number;
}

interface NavigationPathItem {
  userId: number;
  username: string;
  fullName: string;
  level: number;
}

interface CommissionNavigationState {
  selectedUserId: number | null;
  navigationPath: NavigationPathItem[];
}

const MultiLevelCommissionManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Navigation state
  const [navState, setNavState] = useState<CommissionNavigationState>({
    selectedUserId: null,
    navigationPath: []
  });

  // Fetch hierarchical data
  const { 
    data: hierarchicalData, 
    isLoading, 
    error,
    refetch 
  } = useQuery<HierarchicalDownlineResponseDto>({
    queryKey: ['hierarchical-downlines-full'],
    queryFn: async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/hierarchical-downlines?maxDepth=10&includeOperatorCommissions=true`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch hierarchical downlines');
      }
      
      return response.json();
    }
  });

  // Initialize navigation path when data loads
  useEffect(() => {
    if (hierarchicalData && navState.navigationPath.length === 0) {
      setNavState(prev => ({
        ...prev,
        navigationPath: [{
          userId: hierarchicalData.currentUserId,
          username: hierarchicalData.currentUsername,
          fullName: hierarchicalData.currentFullName,
          level: hierarchicalData.currentLevel
        }],
        selectedUserId: hierarchicalData.currentUserId
      }));
    }
  }, [hierarchicalData]);


  // Get current user's downlines based on navigation path
  const getCurrentDownlines = useCallback((): HierarchicalDownlineDto[] => {
    if (!hierarchicalData) return [];
    
    // If viewing root (yourself)
    if (navState.selectedUserId === hierarchicalData.currentUserId) {
      return hierarchicalData.directDownlines;
    }

    // Navigate down the tree to find the selected user
    const findDownlines = (downlines: HierarchicalDownlineDto[]): HierarchicalDownlineDto[] => {
      for (const downline of downlines) {
        if (downline.userId === navState.selectedUserId) {
          return downline.downlines;
        }
        if (downline.downlines.length > 0) {
          const found = findDownlines(downline.downlines);
          if (found.length > 0 || downlines.some(d => d.userId === navState.selectedUserId)) {
            return found;
          }
        }
      }
      return [];
    };

    return findDownlines(hierarchicalData.directDownlines);
  }, [hierarchicalData, navState.selectedUserId]);


  // Handle breadcrumb navigation
  const handleBreadcrumbClick = useCallback((userId: number) => {
    const index = navState.navigationPath.findIndex(item => item.userId === userId);
    if (index !== -1) {
      setNavState(prev => ({
        ...prev,
        selectedUserId: userId,
        navigationPath: prev.navigationPath.slice(0, index + 1)
      }));
    }
  }, [navState.navigationPath]);

  // Handle drill down (click on a downline to view their downlines)
  const handleDrillDown = useCallback((downline: HierarchicalDownlineDto) => {
    setNavState(prev => ({
      ...prev,
      selectedUserId: downline.userId,
      navigationPath: [...prev.navigationPath, {
        userId: downline.userId,
        username: downline.username,
        fullName: downline.fullName,
        level: downline.hierarchyLevel
      }]
    }));
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load commission data: {error.message}
      </Alert>
    );
  }

  if (!hierarchicalData) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No commission data available.
      </Alert>
    );
  }

  const currentDownlines = getCurrentDownlines();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Breadcrumb Navigation */}
      <CommissionBreadcrumb
        navigationPath={navState.navigationPath}
        onBreadcrumbClick={handleBreadcrumbClick}
        currentDownlineCount={currentDownlines.length}
      />

      {/* Main Content Area - Full Width */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        position: 'relative'
      }}>
        <DownlineDetailsPanel
          downlines={currentDownlines}
          currentUser={navState.navigationPath[navState.navigationPath.length - 1]}
          onDrillDown={handleDrillDown}
          onEditCommission={() => {}}
          onRefresh={refetch}
        />
      </Box>
    </Box>
  );
};

export default MultiLevelCommissionManagement;

